import { useCallback, useEffect, useRef, useState } from 'react';

/** How far (px, after damping) the feed must be pulled before letting go refreshes. */
export const PULL_THRESHOLD = 64;
/** The indicator stops following the finger here. */
export const PULL_MAX = 96;
/** The spinner stays at least this long, so a fast refresh still reads as one. */
const MIN_SPIN_MS = 450;

/** Finger travel to indicator travel: half speed, so the pull feels weighted, capped at PULL_MAX. */
export function dampPull(dy: number): number {
  if (dy <= 0) return 0;
  return Math.min(PULL_MAX, dy * 0.5);
}

export interface PullState {
  /** Indicator travel in px (0 when idle). */
  pull: number;
  refreshing: boolean;
}

/**
 * Pull down at the top of the page-scrolled feed to refetch. The library page
 * sets `overscroll-behavior: none`, which also switches off Safari's own pull
 * to refresh, and a home-screen app never had one, so the feed draws its own.
 *
 * Listens on the window (the feed scrolls the page, not a box). A pull only
 * starts from a touch inside `rootRef` while the page is at the top, and only
 * when the finger moves more down than sideways, so swipes and scrolling
 * are left alone. Listeners are passive: nothing here blocks scrolling.
 */
export function usePullToRefresh(
  onRefresh: () => Promise<void>,
  rootRef: React.RefObject<HTMLElement>,
  enabled: boolean,
): PullState {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const active = useRef(false);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const setBoth = useCallback((value: number) => {
    pullRef.current = value;
    setPull(value);
  }, []);

  useEffect(() => {
    if (!enabled) {
      start.current = null;
      active.current = false;
      if (!refreshingRef.current) setBoth(0);
      return;
    }

    const onStart = (e: TouchEvent) => {
      if (refreshingRef.current || e.touches.length !== 1) return;
      const target = e.target as Node | null;
      if (!target || !rootRef.current?.contains(target)) return;
      if (window.scrollY > 0) return;
      start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      active.current = false;
    };

    const onMove = (e: TouchEvent) => {
      const s = start.current;
      if (!s || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - s.x;
      const dy = e.touches[0].clientY - s.y;
      if (!active.current) {
        // Wait for a clear direction; sideways or upward means it isn't a pull
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || window.scrollY > 0) {
          start.current = null;
          return;
        }
        active.current = true;
      }
      setBoth(dampPull(dy));
    };

    const onEnd = () => {
      const wasActive = active.current;
      start.current = null;
      active.current = false;
      if (!wasActive) return;
      if (pullRef.current < PULL_THRESHOLD) {
        setBoth(0);
        return;
      }
      refreshingRef.current = true;
      setRefreshing(true);
      setBoth(PULL_THRESHOLD);
      const began = Date.now();
      Promise.resolve()
        .then(() => onRefreshRef.current())
        .catch((err) => console.warn('[MobileLibrary] Pull to refresh failed', err))
        .then(() => new Promise((r) => setTimeout(r, Math.max(0, MIN_SPIN_MS - (Date.now() - began)))))
        .finally(() => {
          refreshingRef.current = false;
          setRefreshing(false);
          setBoth(0);
        });
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, rootRef, setBoth]);

  return { pull, refreshing };
}
